/**
 * Content + layout guards for the login screen — split-journey layout:
 * a persistent illustrated/carousel panel (side on web, top on mobile) next
 * to a lean phone-entry form. No chessboard background, no stepper.
 */

export type CarouselSlide = {
  key: string;
  glyph: string; // raw chess codepoint (never emoji-presentation)
  title: string;
  sub: string;
};

export const LOGIN_HERO = {
  // Desktop form headline/support (carousel lives in the persistent side panel).
  headline: "Let's get you playing",
  support: "Sign in with your phone — we'll text a one-time code.",
  // Mobile form's single support line (carousel lives in the top panel).
  instruction: "We'll text you a one-time code — no password needed.",
} as const;

/**
 * Feature showcase for the login carousel — one slide per product surface.
 * Shows one card at a time and auto-advances.
 */
export const LOGIN_CAROUSEL: readonly CarouselSlide[] = [
  {
    key: 'play',
    glyph: '♞',
    title: 'Play rated games',
    sub: 'Match with young players worldwide, any time control',
  },
  {
    key: 'coach',
    glyph: '♛',
    title: 'Caissa, your AI coach',
    sub: 'Hints, nudges and guidance while you play and learn',
  },
  {
    key: 'visualisation',
    glyph: '♝',
    title: 'Visualization trainer',
    sub: 'Calculate moves ahead without touching the pieces',
  },
  {
    key: 'reports',
    glyph: '♟',
    title: 'AI-curated reports',
    sub: 'Your games evaluated into a personal improvement report',
  },
  {
    key: 'review',
    glyph: '♜',
    title: 'Review every game',
    sub: 'Move-by-move analysis that shows you the turning points',
  },
  {
    key: 'tournaments',
    glyph: '♚',
    title: 'Tournaments',
    sub: 'Daily and weekend events with the community',
  },
] as const;

/** ~3s per slide reads comfortably without feeling stuck. */
export const CAROUSEL_INTERVAL_MS = 3000;

/** "Sent to +91 98…" subline for the OTP step; empty when we have no number. */
export const formatSentTo = (countryCode: string, mobile: string): string => {
  if (!mobile) {
    return '';
  }
  return ['Sent to', countryCode, mobile].filter(Boolean).join(' ');
};
